import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Space, Flex, Select, Button } from 'antd';
import { MenuItemLookup } from '../../types/menutypes';
import { lookupMenuItems } from '../../services/menuservice';
import debounce from 'lodash/debounce';
import { useAuth } from '../../auth/authcontext';

const { Option } = Select;

interface ItemSelectionDrawerProps {
    open: boolean;
    onClose: () => void;
    onAssign: (menuGuid: string) => Promise<void> | void;
}

const ItemSelectionDrawer: React.FC<ItemSelectionDrawerProps> = ({
    open,
    onClose,
    onAssign,
}) => {
    const { isReadOnly } = useAuth();
    const [source, setSource] = useState<"menu" | "product" | null>(null);
    const [items, setItems] = useState<MenuItemLookup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [selectedMenuGuid, setSelectedMenuGuid] = useState<string>("");

    useEffect(() => {
        if (open) {
            setSource(null);
            setItems([]);
            setSelectedMenuGuid("");
        }
    }, [open]);

    const fetchLookupItems = async (query = "", selectedSource = source) => {
        if (!selectedSource) return;
        setLoadingItems(true);
        try {
            const res = await lookupMenuItems(selectedSource, query, 50);
            setItems(res.items);
        } catch (error) {
            console.error("Failed to load lookup items", error);
        } finally {
            setLoadingItems(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce((value: string) => fetchLookupItems(value), 300),
        [source]
    );

    const handleSourceChange = (val: "menu" | "product") => {
        setSource(val);
        setItems([]);
        setSelectedMenuGuid("");
        fetchLookupItems("", val);
    };

    const handleSelectItem = (menuGuid: string) => {
        setSelectedMenuGuid(menuGuid);
    };

    const handleAssign = async () => {
        if (!selectedMenuGuid) return;
        await onAssign(selectedMenuGuid);
        onClose();
    };

    return (
        <Drawer open={open} onClose={onClose} placement="right" title="Assign Item">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Flex gap="bottom" vertical>
                    <label className="form-label">Source:</label>
                    <Select
                        placeholder="Select source"
                        value={source || undefined}
                        onChange={handleSourceChange}
                        style={{ width: '100%' }}
                    >
                        <Option value="menu">Menu</Option>
                        <Option value="product">Product</Option>
                    </Select>
                </Flex>

                <Flex gap="bottom" vertical>
                    <label className="form-label">Item:</label>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Search by name or UPC"
                        allowClear
                        value={selectedMenuGuid || undefined}
                        disabled={!source}
                        loading={loadingItems}
                        filterOption={false}
                        onSearch={debouncedSearch}
                        onDropdownVisibleChange={(open) => {
                            if (open && items.length === 0 && source) {
                                fetchLookupItems("");
                            }
                        }}
                        onChange={handleSelectItem}
                    >
                        {items.map((option) => (
                            <Option key={option.menuGuid} value={option.menuGuid}>
                                {option.name}
                            </Option>
                        ))}
                    </Select>
                </Flex>

                <Flex>
                    <Space direction="horizontal" size="middle">
                        {!isReadOnly && (
                            <Button
                                color="green"
                                type="primary"
                                variant="solid"
                                size="small"
                                style={{
                                    marginTop: '20px',
                                    paddingInline: '15px',
                                    fontSize: '12px',
                                    width: '100px',
                                }}
                                disabled={!selectedMenuGuid}
                                onClick={handleAssign}
                            >
                                Assign
                            </Button>
                        )}
                        <Button
                            variant="solid"
                            size="small"
                            style={{
                                marginTop: '20px',
                                paddingInline: '15px',
                                fontSize: '12px',
                                width: '100px',
                            }}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </Space>
                </Flex>
            </Space>
        </Drawer>
    );
};

export default ItemSelectionDrawer;
